/**
 * 홈 페이지 (학생용)
 * 배정된 문제집 목록 표시
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { PixelButton } from '../components/common/PixelButton';
import { PixelCard } from '../components/common/PixelCard';
import { WorkbookList } from '../components/workbooks/WorkbookList';
import { AssignedWorkbook } from '../types/workbook';
import { getMyAssignments } from '../services/workbookService';

/**
 * HomePage 컴포넌트
 *
 * - 배정된 문제집 목록 조회 및 표시
 * - 진행중/완료 문제집 분리
 * - 문제집 선택 시 게임 세션 시작
 */
export const HomePage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { startSession } = useGameStore();
  const navigate = useNavigate();

  // 상태 관리
  const [assignments, setAssignments] = useState<AssignedWorkbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 배정된 문제집 목록 조회
   */
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        setLoading(true);
        setError(null);

        // user.id가 없으면 에러
        if (!user?.id) {
          throw new Error('사용자 정보가 없습니다');
        }

        const response = await getMyAssignments(user.id);
        console.log('📚 API Response:', response);
        console.log('📚 Assignments:', response.assignments);
        if (response.assignments.length > 0) {
          console.log('📚 First assignment:', response.assignments[0]);
          console.log('📚 Workbooks field:', response.assignments[0].workbooks);
        }
        setAssignments(response.assignments);
      } catch (err: any) {
        console.error('Failed to fetch assignments:', err);
        setError(
          err.response?.data?.detail ||
            err.message ||
            '문제집 목록을 불러오는데 실패했습니다'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [user]);

  /**
   * 로그아웃 핸들러
   */
  const handleLogout = () => {
    logout();
  };

  /**
   * 문제집 선택 핸들러
   * @param workbookName - 선택된 문제집 이름
   */
  const handleSelectWorkbook = (workbookName: string) => {
    // 페이지 수 제한 없음 (학생이 직접 선택)
    const totalPages = 0;

    // 게임 세션 시작
    startSession(workbookName, totalPages);

    // 문제 풀이 페이지로 이동
    navigate(`/workbooks/${encodeURIComponent(workbookName)}/solve`);
  };

  return (
    <div className="min-h-screen bg-black p-4">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <PixelCard variant="panel" padding="medium">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-pixel text-[#9bbc0f] mb-2">
                  🎮 채점톡
                </h1>
                <p className="text-gray-400 font-pixel text-sm">
                  안녕하세요, {user?.name}님!
                </p>
              </div>
              <PixelButton variant="secondary" size="small" onClick={handleLogout}>
                로그아웃
              </PixelButton>
            </div>
          </PixelCard>
        </div>

        {/* 문제집 목록 */}
        <PixelCard variant="screen" padding="large">
          {/* 로딩 상태 */}
          {loading && (
            <div className="text-center py-12">
              <p className="font-pixel text-lg animate-pulse text-[#9bbc0f]">
                로딩중...
              </p>
            </div>
          )}

          {/* 에러 상태 */}
          {!loading && error && (
            <div className="text-center py-12">
              <p className="font-pixel text-lg mb-4 text-[#ff6b6b]">⚠️</p>
              <p className="font-pixel text-sm text-[#ff6b6b]">{error}</p>
              <PixelButton
                variant="secondary"
                size="small"
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                새로고침
              </PixelButton>
            </div>
          )}

          {/* 빈 상태 (배정된 문제집 없음) */}
          {!loading && !error && assignments.length === 0 && (
            <div className="text-center py-12">
              <p className="font-pixel text-4xl mb-4">📚</p>
              <p className="font-pixel text-lg text-[#9bbc0f] mb-2">
                배정된 문제집이 없습니다
              </p>
              <p className="font-pixel text-xs text-gray-500">
                선생님이 문제집을 배정할 때까지 기다려주세요
              </p>
            </div>
          )}

          {/* 문제집 목록 */}
          {!loading && !error && assignments.length > 0 && (
            <WorkbookList
              assignments={assignments}
              onSelectWorkbook={handleSelectWorkbook}
            />
          )}
        </PixelCard>

        {/* 하단 액션 버튼 */}
        <div className="mt-6 text-center">
          <PixelButton
            variant="primary"
            size="medium"
            onClick={() => navigate('/result-search')}
          >
            📊 채점 결과 조회
          </PixelButton>
        </div>
      </div>
    </div>
  );
};
